import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, Save, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { T, knopfStil, primaerKnopfStil } from '../../lib/designTokens';

const CONTRACT_TYPE_OPTIONS = [
  { value: 'kaufvertrag-kleinspeicher', label: 'Kaufvertrag Kleinspeicher' },
  { value: 'projektrechte-pv', label: 'Projektrechte PV Kaufvertrag' },
  { value: 'dachnutzungsvertrag-sanierung', label: 'Dachnutzungsvertrag Sanierung' },
  { value: 'pachtvertrag-dachverpachtung-ez', label: 'Pachtvertrag Dachverpachtung (EZ)' },
  { value: 'pachtvertrag-dachverpachtung-jz', label: 'Pachtvertrag Dachverpachtung (JZ)' },
  { value: 'pachtvertrag-kleinspeicher-ez', label: 'Pachtvertrag Kleinspeicher (EZ)' },
  { value: 'pachtvertrag-kleinspeicher-jz', label: 'Pachtvertrag Kleinspeicher (JZ)' },
  { value: 'aufhebungsvereinbarung', label: 'Aufhebungsvereinbarung' },
  { value: 'kuendigung-eigentuemervertrag', label: 'Kündigung Eigentümervertrag' },
  { value: 'werkliefervertrag-pv', label: 'Werkliefervertrag PV' },
  { value: 'standarddoc-grundbuchauszug', label: 'Anschreiben Grundbuchauszug' },
  { value: 'standarddoc-bauunterlagen', label: 'Anschreiben Bauunterlagen' },
  { value: 'standarddoc-grundbucheintragung', label: 'Anschreiben Grundbucheintragung' },
  { value: 'standarddoc-verfahrensfreiheit-st', label: 'Anfrage Verfahrensfreiheit (Sachsen-Anhalt)' },
  { value: 'standarddoc-verfahrensfreiheit-by', label: 'Anfrage Verfahrensfreiheit (Bayern)' },
  { value: 'standarddoc-verfahrensfreiheit-bw', label: 'Anfrage Verfahrensfreiheit (Baden-Württemberg)' },
  { value: 'standarddoc-verfahrensfreiheit-ni', label: 'Anfrage Verfahrensfreiheit (Niedersachsen)' },
  { value: 'standarddoc-verfahrensfreiheit-nrw', label: 'Anfrage Verfahrensfreiheit (Nordrhein-Westfalen)' },
  { value: 'standarddoc-verfahrensfreiheit-sl', label: 'Anfrage Verfahrensfreiheit (Saarland)' },
  { value: 'standarddoc-verfahrensfreiheit-sh', label: 'Anfrage Verfahrensfreiheit (Schleswig-Holstein)' },
  { value: 'standarddoc-verfahrensfreiheit-th', label: 'Anfrage Verfahrensfreiheit (Thüringen)' },
];

// Vertraege, aus denen Beispiel-Paragraphen stammen koennen, die aber nicht in
// der Auswahl "Sichtbar fuer Vertragsarten" stehen - nur fuer die Anzeige der
// Herkunft (Code-Review 23.09.2026: sonst stand dort wieder die Kennung).
const WEITERE_VERTRAGSNAMEN = {
  vermittlervertrag: 'Vermittlervertrag',
  'pachtvertrag-grossspeicher-ez': 'Pachtvertrag Großspeicher (EZ)',
  'pachtvertrag-grossspeicher-jz': 'Pachtvertrag Großspeicher (JZ)',
};

const INSERT_POSITION_OPTIONS = [
  { value: 'before-signatures', label: 'Vor Unterschriften (Standard)' },
  { value: 'after-preamble', label: 'Nach der Präambel' },
  { value: 'end-of-contract', label: 'Ende des Vertrags (vor Anlagen)' },
];

function normalizeClauseContractType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  const key = raw
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const aliases = {
    'ks-ez': 'pachtvertrag-kleinspeicher-ez',
    'kleinspeicher-ez': 'pachtvertrag-kleinspeicher-ez',
    'kleinspeicher-einmalzahlung': 'pachtvertrag-kleinspeicher-ez',
    'kleinspeicher-einmalige-zahlung': 'pachtvertrag-kleinspeicher-ez',
    'pachtvertrag-ks-ez': 'pachtvertrag-kleinspeicher-ez',
    'pachtvertrag-kleinspeicher-einmalzahlung': 'pachtvertrag-kleinspeicher-ez',
    'pachtvertrag-kleinspeicher-einmalige-zahlung': 'pachtvertrag-kleinspeicher-ez',
    'ks-jz': 'pachtvertrag-kleinspeicher-jz',
    'kleinspeicher-jz': 'pachtvertrag-kleinspeicher-jz',
    'kleinspeicher-jahreszahlung': 'pachtvertrag-kleinspeicher-jz',
    'kleinspeicher-jaehrliche-zahlung': 'pachtvertrag-kleinspeicher-jz',
    'kleinspeicher-jahrliche-zahlung': 'pachtvertrag-kleinspeicher-jz',
    'pachtvertrag-ks-jz': 'pachtvertrag-kleinspeicher-jz',
    'pachtvertrag-kleinspeicher-jahreszahlung': 'pachtvertrag-kleinspeicher-jz',
    'pachtvertrag-kleinspeicher-jaehrliche-zahlung': 'pachtvertrag-kleinspeicher-jz',
    'pachtvertrag-kleinspeicher-jahrliche-zahlung': 'pachtvertrag-kleinspeicher-jz',
    'projektrechte': 'projektrechte-pv',
    'projektrecht': 'projektrechte-pv',
    'projektrechte-pv': 'projektrechte-pv',
    'projektrecht-pv': 'projektrechte-pv',
    'pv-projektrechte': 'projektrechte-pv',
    'pv-projektrecht': 'projektrechte-pv',
    'kaufvertrag-projektrechte-pv': 'projektrechte-pv',
    'projektrechte-pv-kaufvertrag': 'projektrechte-pv',
    'projektverkauf-pv': 'projektrechte-pv',
    'pv-ez': 'pachtvertrag-dachverpachtung-ez',
    'dachverpachtung-ez': 'pachtvertrag-dachverpachtung-ez',
    'dachnutzung-ez': 'pachtvertrag-dachverpachtung-ez',
    'pachtvertrag-dachnutzung-ez': 'pachtvertrag-dachverpachtung-ez',
    'pachtvertrag-dachverpachtung-einmalzahlung': 'pachtvertrag-dachverpachtung-ez',
    'pv-jz': 'pachtvertrag-dachverpachtung-jz',
    'dachverpachtung-jz': 'pachtvertrag-dachverpachtung-jz',
    'dachnutzung-jz': 'pachtvertrag-dachverpachtung-jz',
    'pachtvertrag-dachnutzung-jz': 'pachtvertrag-dachverpachtung-jz',
    'pachtvertrag-dachverpachtung-jahreszahlung': 'pachtvertrag-dachverpachtung-jz',
    'dachnutzungsvertrag-mit-sanierung': 'dachnutzungsvertrag-sanierung',
  };
  return aliases[key] || key;
}

function normalizeClauseContractTypes(value) {
  const rawItems = Array.isArray(value)
    ? value
    : (typeof value === 'string' ? value.split(/[;,|]/) : []);
  return Array.from(new Set(rawItems.map(normalizeClauseContractType).filter(Boolean)));
}

function decodeClauseHtmlEntities(value) {
  const raw = String(value || '');
  if (typeof document === 'undefined') return raw;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = raw;
  return textarea.value;
}

function normalizeClausePlaceholderName(value) {
  return decodeClauseHtmlEntities(value)
    .replace(/[｛]/g, '{')
    .replace(/[｝]/g, '}')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function collectClausePlaceholdersFromText(value, placeholders) {
  const text = String(value || '')
    .replace(/[｛]/g, '{')
    .replace(/[｝]/g, '}');
  text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, key) => {
    const normalized = normalizeClausePlaceholderName(key);
    if (normalized) placeholders.add(normalized);
    return _match;
  });
}

function plainTextFromClauseHtml(value) {
  const html = String(value || '');
  if (!html || typeof document === 'undefined') return '';
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.textContent || '';
}

function extractClausePlaceholders(content) {
  const placeholders = new Set();
  const raw = String(content || '');
  const decoded = decodeClauseHtmlEntities(raw);
  collectClausePlaceholdersFromText(raw, placeholders);
  collectClausePlaceholdersFromText(decoded, placeholders);
  collectClausePlaceholdersFromText(plainTextFromClauseHtml(decoded), placeholders);
  return Array.from(placeholders).sort((a, b) => a.localeCompare(b, 'de'));
}

function normalizeClauseDefaultMapping(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result = {};
  Object.entries(value).forEach(([placeholder, rawMapping]) => {
    const key = normalizeClausePlaceholderName(placeholder);
    const mapping = String(rawMapping || '').trim();
    if (key && mapping) result[key] = mapping;
  });
  return result;
}

function setClauseDefaultMapping(clause, placeholder, mappingValue) {
  const current = normalizeClauseDefaultMapping(clause.default_mapping || clause.defaultMapping);
  const next = { ...current };
  const key = normalizeClausePlaceholderName(placeholder);
  const value = String(mappingValue || '').trim();
  if (key && value) {
    next[key] = value;
  } else if (key) {
    delete next[key];
  }
  return { ...clause, default_mapping: next };
}

function emptyClause() {
  return {
    id: `new-${Date.now()}`,
    title: '',
    content: '',
    insert_position: 'before-signatures',
    contract_positions: {},
    contract_types: [],
    default_mapping: {},
    sort_order: 0,
    is_active: true,
    _isNew: true,
  };
}

function getContractTypeLabel(value) {
  return CONTRACT_TYPE_OPTIONS.find((option) => option.value === value)?.label || value;
}

function getContractPosition(clause, contractType) {
  const normalizedContractType = normalizeClauseContractType(contractType);
  const positions = clause.contract_positions && typeof clause.contract_positions === 'object'
    ? clause.contract_positions
    : {};
  const current = Object.entries(positions).reduce((found, [key, value]) => {
    if (found || normalizeClauseContractType(key) !== normalizedContractType || !value || typeof value !== 'object') return found;
    return value;
  }, null) || {};
  return {
    section_number: String(current.section_number || current.section || '').trim(),
    insert_mode: current.insert_mode === 'index' ? 'index' : 'append',
    item_number: String(current.item_number || current.item || '').trim(),
  };
}

function setContractPosition(clause, contractType, patch) {
  const positions = clause.contract_positions && typeof clause.contract_positions === 'object'
    ? clause.contract_positions
    : {};
  const current = getContractPosition(clause, contractType);
  return {
    ...clause,
    contract_positions: {
      ...positions,
      [contractType]: {
        ...current,
        ...patch,
      },
    },
  };
}

function getContractPositionSummary(clause) {
  const types = normalizeClauseContractTypes(clause.contract_types);
  return types
    .map((type) => {
      const position = getContractPosition(clause, type);
      if (!position.section_number) return '';
      const suffix = position.insert_mode === 'index' && position.item_number
        ? `Nr. ${position.item_number}`
        : 'letzter Punkt';
      return `${getContractTypeLabel(type)}: Paragraph ${position.section_number}, ${suffix}`;
    })
    .filter(Boolean);
}

function ClauseRow({ clause, index, total, onChange, onDelete, onMoveUp, onMoveDown }) {
  const [expanded, setExpanded] = useState(clause._isNew || false);
  const [manualMappingPlaceholder, setManualMappingPlaceholder] = useState('');
  const normalizedContractTypes = normalizeClauseContractTypes(clause.contract_types);
  const clauseDefaultMapping = normalizeClauseDefaultMapping(clause.default_mapping || clause.defaultMapping);
  clause = { ...clause, contract_types: normalizedContractTypes, default_mapping: clauseDefaultMapping };
  const positionSummaries = getContractPositionSummary(clause);
  const manualMappingKey = normalizeClausePlaceholderName(manualMappingPlaceholder);
  const clausePlaceholders = useMemo(() => {
    const keys = new Set([
      ...extractClausePlaceholders(clause.content),
      ...Object.keys(clauseDefaultMapping).map(normalizeClausePlaceholderName).filter(Boolean),
    ]);
    if (manualMappingKey) keys.add(manualMappingKey);
    return Array.from(keys).sort((a, b) => a.localeCompare(b, 'de'));
  }, [clause.content, clauseDefaultMapping, manualMappingKey]);
  const mayContainPlaceholderSyntax = /(\{\{|&#123;|&#x7b;|&lbrace;|&lcub;|｛)/i.test(String(clause.content || ''));
  // Woher ein Beispiel-Paragraph stammt. Bis zum 23.09.2026 stand das in der
  // Kopfzeile jeder Karte als Kette interner Kennungen ("pachtvertrag-...-jz
  // · ci · custom-mpc524v8-33 · (after) · ..."). Lesbar sind nur Vertrag und
  // Abschnitt; die Kennungen stehen noch im Tooltip, fuer die Fehlersuche.
  const herkunftTechnisch = [
    clause.source_contract_key,
    clause.source_variant_key,
    clause.source_anchor_block_id || clause.source_block_id,
    clause.source_anchor_side ? `(${clause.source_anchor_side})` : '',
    clause.source_anchor_label || clause.source_structure_label || clause.source_block_type || clause.source_class_name,
  ].filter(Boolean).join(' · ');
  const herkunft = clause.source_contract_key
    ? [WEITERE_VERTRAGSNAMEN[clause.source_contract_key] || getContractTypeLabel(clause.source_contract_key), clause.source_section_label].filter(Boolean).join(', ')
    : '';

  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, background: clause.is_active ? '#FFFFFF' : '#F8FAFC', marginBottom: 8 }}>
      {/* Header row */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <GripVertical size={14} color="#94A3B8" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: clause.is_active ? '#0F172A' : '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {clause.title || <span style={{ color: '#CBD5E1', fontStyle: 'italic' }}>Kein Titel</span>}
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
            {[
              (INSERT_POSITION_OPTIONS.find((o) => o.value === clause.insert_position)?.label || clause.insert_position || '').replace(' (Standard)', ''),
              positionSummaries.length === 1 ? '1 genaue Position' : (positionSummaries.length > 1 ? `${positionSummaries.length} genaue Positionen` : ''),
              clause.contract_types.length === 0 ? 'alle Verträge' : (clause.contract_types.length === 1 ? getContractTypeLabel(clause.contract_types[0]) : `${clause.contract_types.length} Vertragsarten`),
            ].filter(Boolean).join(' · ')}
          </div>
          {herkunft ? (
            <div title={herkunftTechnisch} style={{ fontSize: 10.5, color: T.faint, marginTop: 2 }}>
              aus {herkunft}
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            title={clause.is_active ? 'Aktiv — klicken zum Deaktivieren' : 'Inaktiv — klicken zum Aktivieren'}
            onClick={() => onChange({ ...clause, is_active: !clause.is_active })}
            style={{ padding: '4px 6px', border: '1px solid #E2E8F0', borderRadius: 6, background: '#FFFFFF', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            {clause.is_active ? <Eye size={13} color="#16A34A" /> : <EyeOff size={13} color="#94A3B8" />}
          </button>
          <button onClick={() => onMoveUp(index)} disabled={index === 0} aria-label="Nach oben" style={{ padding: '4px 6px', border: '1px solid #E2E8F0', borderRadius: 6, background: '#FFFFFF', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.4 : 1 }}>
            <ChevronUp size={13} />
          </button>
          <button onClick={() => onMoveDown(index)} disabled={index === total - 1} aria-label="Nach unten" style={{ padding: '4px 6px', border: '1px solid #E2E8F0', borderRadius: 6, background: '#FFFFFF', cursor: index === total - 1 ? 'not-allowed' : 'pointer', opacity: index === total - 1 ? 0.4 : 1 }}>
            <ChevronDown size={13} />
          </button>
          <button onClick={() => onDelete(clause.id)} title="Paragraph löschen" aria-label="Paragraph löschen" style={{ padding: '4px 6px', border: '1px solid #FEE2E2', borderRadius: 6, background: '#FFF5F5', cursor: 'pointer' }}>
            <Trash2 size={13} color="#EF4444" />
          </button>
        </div>
      </div>

      {/* Expandable edit form */}
      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid #F1F5F9', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Titel</label>
            <input
              value={clause.title}
              onChange={(e) => onChange({ ...clause, title: e.target.value })}
              placeholder="z.B. Stellflächenregelung"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
              Paragraphen-Text (HTML erlaubt)
            </label>
            <textarea
              value={clause.content}
              onChange={(e) => onChange({ ...clause, content: e.target.value })}
              placeholder='<p class="clause-paragraph">Es wird festgehalten, dass pro 250 kW Kleinspeicher eine reine Stellfläche von maximal 20 m² zur Verfügung steht.</p>'
              rows={5}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box', lineHeight: 1.5, resize: 'vertical' }}
            />
          </div>
          {clausePlaceholders.length > 0 || mayContainPlaceholderSyntax ? (
            <div style={{ padding: 10, border: '1px solid #DBEAFE', borderRadius: 8, background: '#EFF6FF', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#122A5C' }}>
                  Feldzuordnung der Platzhalter
                </label>
                <div style={{ color: '#475569', fontSize: 11, lineHeight: 1.4, marginTop: 3 }}>
                  Gilt, wenn der Paragraph gewählt ist – das Vertragsmapping hat Vorrang.
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.9fr) minmax(180px, 1.4fr)', gap: 8, alignItems: 'center' }}>
                <input
                  value={manualMappingPlaceholder}
                  onChange={(event) => setManualMappingPlaceholder(event.target.value)}
                  placeholder="Platzhalter manuell, z.B. pro kwp euro"
                  style={{ width: '100%', padding: '7px 8px', border: '1px solid #93C5FD', borderRadius: 7, fontSize: 12, boxSizing: 'border-box', background: '#FFFFFF' }}
                />
                <div style={{ color: '#64748B', fontSize: 11, lineHeight: 1.35 }}>
                  Falls der Platzhalter nicht automatisch erscheint, trage hier nur den Namen ohne geschweifte Klammern ein.
                </div>
              </div>
              {clausePlaceholders.length === 0 ? (
                <div style={{ color: '#B45309', fontSize: 11, lineHeight: 1.4 }}>
                  Im Text sieht es nach einem Platzhalter aus, aber es wurde noch kein eindeutiger Name erkannt. Bitte oben manuell eintragen.
                </div>
              ) : null}
              {clausePlaceholders.map((placeholder) => (
                <div key={placeholder} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.9fr) minmax(180px, 1.4fr)', gap: 8, alignItems: 'center' }}>
                  <code style={{ color: '#122A5C', fontSize: 12, overflowWrap: 'anywhere' }}>{`{{${placeholder}}}`}</code>
                  <input
                    list="bitrix-field-hints"
                    value={clauseDefaultMapping[placeholder] || ''}
                    onChange={(event) => onChange(setClauseDefaultMapping(clause, placeholder, event.target.value))}
                    placeholder="z.B. UF_CRM_1234567 oder calc(UF_CRM_... / 250)"
                    style={{ width: '100%', padding: '7px 8px', border: '1px solid #93C5FD', borderRadius: 7, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box', background: '#FFFFFF' }}
                  />
                </div>
              ))}
            </div>
          ) : null}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Einfügeposition</label>
            <select
              value={clause.insert_position}
              onChange={(e) => onChange({ ...clause, insert_position: e.target.value })}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
            >
              {INSERT_POSITION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
              Sichtbar für Vertragsarten <span style={{ fontWeight: 400, fontSize: 11, color: '#94A3B8' }}>(leer = alle)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CONTRACT_TYPE_OPTIONS.map((o) => {
                const checked = normalizedContractTypes.includes(o.value);
                return (
                  <label key={o.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', padding: '4px 8px', border: `1px solid ${checked ? '#122A5C' : '#E2E8F0'}`, borderRadius: 20, background: checked ? '#EEF2FF' : '#FFFFFF', color: checked ? '#122A5C' : '#475569', fontWeight: checked ? 700 : 400 }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? normalizedContractTypes.filter((v) => v !== o.value)
                          : [...normalizedContractTypes, o.value];
                        const currentPositions = clause.contract_positions && typeof clause.contract_positions === 'object'
                          ? clause.contract_positions
                          : {};
                        onChange({
                          ...clause,
                          contract_types: next,
                          contract_positions: checked || currentPositions[o.value]
                            ? currentPositions
                            : {
                                ...currentPositions,
                                [o.value]: { section_number: '', insert_mode: 'append', item_number: '' },
                              },
                        });
                      }}
                      style={{ display: 'none' }}
                    />
                    {o.label}
                  </label>
                );
              })}
            </div>
          </div>
          {normalizedContractTypes.length > 0 ? (
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                Zielposition pro Vertragsart
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {normalizedContractTypes.map((contractType) => {
                  const position = getContractPosition(clause, contractType);
                  return (
                    <div key={contractType} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.4fr) 90px minmax(150px, 1fr) 96px', gap: 8, alignItems: 'center', padding: 10, border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC' }}>
                      <div style={{ minWidth: 0, color: '#122A5C', fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getContractTypeLabel(contractType)}
                      </div>
                      <input
                        value={position.section_number}
                        onChange={(event) => onChange(setContractPosition(clause, contractType, { section_number: event.target.value }))}
                        placeholder="Paragraph"
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }}
                      />
                      <select
                        value={position.insert_mode}
                        onChange={(event) => onChange(setContractPosition(clause, contractType, { insert_mode: event.target.value }))}
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, boxSizing: 'border-box', background: '#FFFFFF' }}
                      >
                        <option value="append">Als letzter Punkt</option>
                        <option value="index">Als Unterpunkt Nr.</option>
                      </select>
                      <input
                        value={position.item_number}
                        onChange={(event) => onChange(setContractPosition(clause, contractType, { item_number: event.target.value }))}
                        placeholder="Nr."
                        disabled={position.insert_mode !== 'index'}
                        style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, boxSizing: 'border-box', opacity: position.insert_mode === 'index' ? 1 : 0.55, background: position.insert_mode === 'index' ? '#FFFFFF' : '#F1F5F9' }}
                      />
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 6, color: '#64748B', fontSize: 11, lineHeight: 1.4 }}>
                Diese genaue Position hat Vorrang vor der allgemeinen Einf?geposition. Bei "Unterpunkt Nr." wird der Passus an diese Nummer gesetzt; nachfolgende nummerierte Punkte werden im selben Paragraphen weitergez?hlt.
              </div>
            </div>
          ) : (
            <div style={{ padding: 10, border: '1px dashed #CBD5E1', borderRadius: 8, background: '#F8FAFC', color: '#64748B', fontSize: 12, lineHeight: 1.4 }}>
              Wähle oben konkrete Vertragsarten aus, um je Vertrag eine genaue Paragraphenposition festzulegen.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClauseManager({ apiBase = '/api', session = null }) {
  const accessToken = session?.access_token || null;
  const authHeaders = useMemo(() => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), [accessToken]);

  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadClauses = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${apiBase}/contracts/state?resource=clauses`, { headers: authHeaders });
      const loaded = Array.isArray(resp.data?.clauses) ? resp.data.clauses : [];
      loaded.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      setClauses(loaded.map((clause) => ({
        ...clause,
        default_mapping: normalizeClauseDefaultMapping(clause.default_mapping || clause.defaultMapping),
      })));
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Klauseln konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => { loadClauses(); }, [loadClauses]);

  const handleAdd = useCallback(() => {
    setClauses((prev) => [...prev, { ...emptyClause(), sort_order: prev.length }]);
  }, []);

  const handleChange = useCallback((updated) => {
    setClauses((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const handleDelete = useCallback((id) => {
    setClauses((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleMoveUp = useCallback((index) => {
    setClauses((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((c, i) => ({ ...c, sort_order: i }));
    });
  }, []);

  const handleMoveDown = useCallback((index) => {
    setClauses((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((c, i) => ({ ...c, sort_order: i }));
    });
  }, []);

  const handleSaveAll = useCallback(async () => {
    setSaving(true);
    try {
      const normalized = clauses.map((c, i) => ({
        ...c,
        default_mapping: normalizeClauseDefaultMapping(c.default_mapping || c.defaultMapping),
        sort_order: i,
        _isNew: undefined,
      }));
      const resp = await axios.post(`${apiBase}/contracts/state?resource=clauses`, { op: 'save-all', clauses: normalized }, { headers: authHeaders });
      const saved = Array.isArray(resp.data?.clauses) ? resp.data.clauses : normalized;
      setClauses(saved);
      toast.success('Alle Klauseln gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [clauses, apiBase, authHeaders]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      {/* Seit dem 23.09.2026 ohne Erklaerzeile - wer hier ist, weiss, was
          optionale Paragraphen sind. */}
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${T.randWeich}`, background: T.grund, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
          Optionale Paragraphen
          {clauses.length ? <span style={{ marginLeft: 8, fontSize: T.tSm, fontWeight: 600, color: T.muted }}>{clauses.length}</span> : null}
        </div>
        <button onClick={handleAdd} style={{ ...knopfStil(), padding: '8px 12px', fontSize: T.tSm, minHeight: 36 }}>
          <Plus size={14} />
          Neuer Paragraph
        </button>
        <button onClick={handleSaveAll} disabled={saving}
          style={{ ...primaerKnopfStil, padding: '8px 16px', fontSize: T.tSm, minHeight: 36, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
          <Save size={14} />
          {saving ? 'Speichert …' : 'Alle speichern'}
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {loading ? (
          <div style={{ color: '#94A3B8', fontSize: 13 }}>Lade Paragraphen…</div>
        ) : clauses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: T.tPanel, fontWeight: 700, marginBottom: 6, color: T.navy }}>Noch keine Paragraphen</div>
            <div style={{ fontSize: T.tText, color: T.muted }}>Über „Neuer Paragraph“ legst du den ersten an.</div>
          </div>
        ) : (
          clauses.map((clause, index) => (
            <ClauseRow
              key={clause.id}
              clause={clause}
              index={index}
              total={clauses.length}
              onChange={handleChange}
              onDelete={handleDelete}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          ))
        )}
      </div>
    </div>
  );
}
